/**
 * The device's copy of its last published anchor, and the movement gate that
 * decides what a background wake owes the server.
 *
 * The restored presence contract (location-roadmap.md, "The presence contract
 * — RESTORED 2026-08-26") in flow order: the device caches the anchor it last
 * published, background wakes sample locally, and the comparison against that
 * anchor — accuracy-aware — decides everything. No meaningful movement means
 * no PostgreSQL update, no venue update, no publish HTTP on the wake; a
 * liveness signal to Redis is all the server needs. Meaningful movement means
 * one publication.
 *
 * One deviation from the contract's letter, recorded in the roadmap: the
 * contract says "the server returns the anchor at check-in", but
 * `GET /venues/current` returns a venue presentation, and the fix the device
 * just successfully published IS byte-for-byte the anchor the server stored.
 * So the device caches its own successful publish instead of a response
 * field — same truth, no API-shape change, works identically for the
 * foreground heartbeat and the background task.
 *
 * **This file imports nothing**, for the reason `stay-visible-diagnostics.ts`
 * gives: `stay-visible.ts` pulls in expo-location and react-native, so
 * anything that wants a real behavioural test has to stay loadable by node.
 * The pure decisions live here; the storage reads and writes stay at the two
 * call sites (`stay-visible.ts`, `session-store.ts`) via
 * `presence-anchor-io.ts`.
 */

export type PresenceAnchor = {
  /**
   * The account this anchor belongs to. Load-bearing, not bookkeeping:
   * the storage key is device-global and `withTimeout` bounds a wedged
   * keychain write WITHOUT cancelling it, so a set that timed out under one
   * account can settle after sign-out's clear and rewrite the key. The next
   * session cannot prevent that write — but it can refuse to READ it, and
   * `parseAnchor` does: an anchor for a different owner is "no anchor",
   * which classifies as publish. Inheritance is rejected at the only point
   * this code controls.
   */
  ownerId: string;
  lat: number;
  lng: number;
  /**
   * Metres. Starts as the published fix's reported accuracy and GROWS under
   * the expanded-uncertainty rule while the device stays quiet: a
   * below-threshold fix at distance `d` with accuracy `a` proves the person
   * is somewhere within `d + a` of the anchor, so that becomes the honest
   * uncertainty. Movement is never laundered into false precision.
   */
  uncertaintyM: number;
  /** ISO time of the publication that set this anchor. */
  publishedAt: string;
};

/**
 * When widening pushes the anchor's uncertainty past this, the anchor is no
 * longer usable and the next wake publishes a fresh fix instead of staying
 * quiet. Pinned to `LAST_KNOWN_MAX_ACCURACY_M` — the discovery radius floor
 * (500 m) — because an anchor vaguer than the tightest radius answers no
 * eligibility question, and the server-side conservative-accuracy check
 * (step 10) would hide the person anyway. Publishing resets uncertainty to
 * the fresh fix's accuracy, which is the honest way out.
 */
export const ANCHOR_UNUSABLE_M = 500;

export type FixClassification =
  | {
      kind: "publish";
      reason: "no_anchor" | "moved" | "anchor_unusable";
      /** Metres from anchor; 0 when there is no anchor to measure from. */
      distanceM: number;
    }
  | {
      kind: "quiet";
      distanceM: number;
      /** The anchor to persist: same position, honestly widened. */
      anchor: PresenceAnchor;
    };

/** Great-circle distance in metres. Standard haversine, R = 6371 km. */
export function distanceMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const rad = Math.PI / 180;
  const dLat = (bLat - aLat) * rad;
  const dLng = (bLng - aLng) * rad;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(aLat * rad) * Math.cos(bLat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.sqrt(s));
}

/**
 * What this wake owes the server.
 *
 * - No anchor: publish. There is nothing to be quiet against.
 * - `d > anchor.uncertaintyM + accuracyM`: publish. The two uncertainty
 *   discs do not overlap, so the movement cannot be measurement noise.
 * - Otherwise the fix is consistent with standing still — but consistent is
 *   not proof. The anchor widens to `max(current, d + accuracyM)`, and if
 *   that honest widening passes `ANCHOR_UNUSABLE_M` the anchor has drifted
 *   into uselessness and the wake publishes fresh instead.
 *
 * The comparisons are strict (`>`): a fix exactly at the combined bound is
 * still explainable by noise, and the cheap mistake here is the quiet one —
 * a wrongly-quiet wake is corrected by the next wake or by the server-side
 * conservative-accuracy check, while a wrongly-published wake is exactly the
 * per-wake write the contract exists to remove.
 */
export function classifyFix(
  anchor: PresenceAnchor | null,
  fix: { lat: number; lng: number; accuracyM: number },
): FixClassification {
  if (!anchor) return { kind: "publish", reason: "no_anchor", distanceM: 0 };

  const d = distanceMeters(anchor.lat, anchor.lng, fix.lat, fix.lng);
  if (d > anchor.uncertaintyM + fix.accuracyM) {
    return { kind: "publish", reason: "moved", distanceM: d };
  }

  const widened = Math.max(anchor.uncertaintyM, d + fix.accuracyM);
  if (widened > ANCHOR_UNUSABLE_M) {
    return { kind: "publish", reason: "anchor_unusable", distanceM: d };
  }

  return {
    kind: "quiet",
    distanceM: d,
    anchor: { ...anchor, uncertaintyM: widened },
  };
}

/**
 * The anchor a successful publication establishes, or null when the fix had
 * no reported accuracy. A null-accuracy publish makes the SERVER's stored
 * anchor unusable for every radius rule (it stores the fix but refuses to
 * reason about it), so the device mirrors that honestly: no cached anchor,
 * and the next wake publishes rather than staying quiet against a position
 * the server cannot use.
 */
export function anchorFromPublishedFix(
  fix: { lat: number; lng: number; accuracyM: number | null },
  ownerId: string,
  publishedAt: string,
): PresenceAnchor | null {
  if (fix.accuracyM === null || !Number.isFinite(fix.accuracyM)) return null;
  if (!ownerId) return null;
  return {
    ownerId,
    lat: fix.lat,
    lng: fix.lng,
    uncertaintyM: fix.accuracyM,
    publishedAt,
  };
}

/**
 * Parse a stored anchor, refusing anything malformed OR owned by somebody
 * else. Both read as "no anchor" (publish on next wake) — the fail-safe
 * direction — never as a position to be quiet against. The ownership check
 * lives HERE, in pure code, so the cross-account rejection is behaviourally
 * tested rather than trusted; the io layer only forwards the expected owner.
 * Anchors stored before `ownerId` existed fail the check like any other
 * malformed value — no migration, they simply re-publish once.
 */
export function parseAnchor(
  raw: string | null,
  expectedOwnerId: string,
): PresenceAnchor | null {
  if (!raw || !expectedOwnerId) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PresenceAnchor>;
    if (
      parsed.ownerId !== expectedOwnerId ||
      typeof parsed.lat !== "number" ||
      !Number.isFinite(parsed.lat) ||
      typeof parsed.lng !== "number" ||
      !Number.isFinite(parsed.lng) ||
      typeof parsed.uncertaintyM !== "number" ||
      !Number.isFinite(parsed.uncertaintyM) ||
      parsed.uncertaintyM < 0 ||
      typeof parsed.publishedAt !== "string"
    ) {
      return null;
    }
    return {
      ownerId: parsed.ownerId,
      lat: parsed.lat,
      lng: parsed.lng,
      uncertaintyM: parsed.uncertaintyM,
      publishedAt: parsed.publishedAt,
    };
  } catch {
    return null;
  }
}

export function serializeAnchor(anchor: PresenceAnchor): string {
  return JSON.stringify(anchor);
}
